export const Hl7SenderDocumentationMarkdown = `
# Send an HL7v2 Message

This block sends an HL7v2 message to the configured destination.

---

#### **IMPORTANT NOTE:** Currently, there is no validation (*coming soon*) to ensure the HL7 message is correct and properly formatted, so it is up to you to make sure. For now, the *HL7 Message Overview* section below should serve as a useful guide to get you started.

---

## Configuration Options

### Destination Datastore
This is the desired destination to send the HL7 message to. It will only show options applicable to the organization this flow is for. Currently, this is based off a hardcoded config
[here](https://github.com/VivaaHealth/vivaa/blob/f9e3ccb1f03b8d4de55f5a29ede293c672208bcb/web/vivaa-api/src/models/flow/hl7/configs.ts#L14-L20).

### Timeout
This is the maximum duration that the block should wait to complete. The block is completed when it receives a callback from the datastore acknowledging that the customer has successfully received the message sent.

## Useful Links
The external HL7 pages below are for version 2.2, so please update it to whichever version you are working with as there could be slight variations to how the message is constructed between different versions.
- [HL7 Message Overview](https://notable-health.atlassian.net/wiki/x/foBjj)
- [Trigger Events](https://hl7-definition.caristix.com/v2/HL7v2.2/TriggerEvents)
- [ADT A08 Message Type](https://hl7-definition.caristix.com/v2/HL7v2.2/TriggerEvents/ADT_A08)
- [MSH Fields](https://hl7-definition.caristix.com/v2/HL7v2.2/Segments/MSH)
- [Message Type Field Definition](https://hl7-definition.caristix.com/v2/HL7v2.2/DataTypes/CM_MSG)
- [HL7 Values Tables](https://hl7-definition.caristix.com/v2/HL7v2.2/Tables)

## HL7 Message Overview

This section summarizes the information from [this](https://notable-health.atlassian.net/wiki/spaces/PRODUCT/pages/2355331198/HL7+Message+Overview) Confluence doc, which contains more details about the structure of HL7 messages as well as links to additional
documentation. 

HL7 messages are defined by **trigger events** and are mainly composed of **segments** and **fields**:

#### Trigger Events
Trigger events represent the type of message being sent. Messages are sent due to the need for data to flow between systems in response to real world events in healthcare. For example, the trigger event "ADT A08" is used to update patient information that has
changed in the EHR.

A breakdown of different trigger events and their standard segments and fields can be found [here](https://hl7-definition.caristix.com/v2/HL7v2.2/TriggerEvents).

#### Segments
Segments represent information about different parts of the message such as the message itself (MSH), a patient (PID), or an order (ORC) on a single line. They always start with a unique, three-character identifier (e.g. MSH) and have standardized structures
composed of fields. The first segment of any message is always the MSH segment.

A breakdown of segments that can be used in an ADT A08 trigger event can be found [here](https://hl7-definition.caristix.com/v2/HL7v2.2/TriggerEvents/ADT_A08).

#### Fields
Fields define the different parts of a segment. They are separated by the \`|\` (pipe) symbol and have standardized formats and data types. Not all fields are required, but empty fields need to be included with the separator (\`||\`).

A breakdown of fields in the MSH segment can be found [here](https://hl7-definition.caristix.com/v2/HL7v2.2/Segments/MSH)

#### Sample HL7 Message

\`\`\`
MSH|^~\\&|NOTABLE|NOTABLE|RECEIVING_APP|RECEIVING_FACILITY|20250101010100||ADT^A08|MSG00001|T|2.3
PID|1||12345^^^MRN||DOE^JOHN^^^^||19800101|M|||
\`\`\`

## Limitations
#### Field validation
The data in fields usually require specific data types, formats, and/or lengths, but there might not be validations done at the time of sending.

For example, the message type field in the MSH segment expects the [Message Type](https://hl7-definition.caristix.com/v2/HL7v2.2/DataTypes/CM_MSG) data type, but does not have validations to check that it is correctly formatted. In this case, we need to
refer to the HL7 documentation to ensure correct formatting. Additionally, it is important to check the documentation for the available coded values for the specific field.

For this message type field, the available values for the first part of the data can be found [here](https://hl7-definition.caristix.com/v2/HL7v2.2/Tables/0076) and for the second part [here](https://hl7-definition.caristix.com/v2/HL7v2.2/Tables/0003) (e.g. "ADT^A08").

This limitation around validation of correct field formatting will be solved in the future when we introduce a better and easier way to construct a message, which will include live validation while you're building the message.

## Errors
#### ApplicationFailure
You will get this failure due to an issue sending the HL7 message. If it's an issue with a specific field, the error message will usually include the data from that field. For example:

- invalid length: a field might require data to be a specific length. An example of this is the date/time of message field in the MSH segment, which requires this length and format: YYYYMMDDHHMMSS (e.g. 20250125123000 for 2025/01/25 12:30:00 PM)

## Common uses cases
Most RPA jobs can be converted to an HL7 block as long as the HL7 setup and configuration is completed with the customer. We can both extract from and export to the EHR. Examples:

- Extract Guarantors
- Extract Patient Information
- Extract Upcoming Patient Insurance
- Export Encounter Information
- Export Patient Information
- Export Patient Order

We can combine these with other platform capabilities to create complex flows, such as a return-to-clinic scheduling flow using forms and an HL7 block to either complete or cancel the patient order, depending on if the patient chose to schedule or not.

## Best practices
While constructing the HL7 message: 
- refer to the various external HL7 resources and documentation linked above to ensure each field and segment in the message is correct.
- try to use outputs from previous blocks as much as possible to prevent having to create multiple variations of the same flow to cater to different parameters.
- use script blocks if needed to transform various data into what is expected for each field.
- make sure to press enter at the end of each segment to start a new segment, as segments are single-line and need to be separated by line breaks.
`;
